"use client";

import { motion, useInView } from "motion/react";
import { useRef, useState } from "react";
import { MessageSquareQuote } from "lucide-react";
import { SectionHeading } from "@/components/landing/section-heading";
import { cn } from "@/lib/utils";

const quotes = [
  {
    quote:
      "Zobliss makes running my spa much easier. I no longer have to juggle separate tools for booking, payments, and marketing; having everything centralized is a game-changer.",
    author: "Stephanie Egger",
    role: "Owner, Serenity Wellness Spa",
    image:
      "https://images.pexels.com/photos/6076102/pexels-photo-6076102.jpeg?auto=compress&cs=tinysrgb&w=800",
  },
  {
    quote:
      "Every time I use the Zobliss automated marketing tools, I see a direct spike in my calendar. The return on investment for my salon has been absolutely worth it.",
    author: "Conrad Geske",
    role: "Studio Manager",
    image:
      "https://images.pexels.com/photos/8468132/pexels-photo-8468132.jpeg?auto=compress&cs=tinysrgb&w=800",
  },
  {
    quote:
      "The way Zobliss handles online booking and automated reminders helped us reduce no-shows significantly. It allowed us to focus on our clients and build the amazing team we have today.",
    author: "Nils Feigenwinter",
    role: "Founder, Luxe MedSpa",
    image:
      "https://images.pexels.com/photos/5531446/pexels-photo-5531446.jpeg?auto=compress&cs=tinysrgb&w=800",
  },
];

function TestimonialCard({ testimonial, index, isActive, onSelect }) {
  const handleSelect = () => onSelect(index);

  return (
    <li
      className={cn(
        "relative flex min-w-0 flex-col gap-6 overflow-hidden rounded-3xl bg-fe-primary/15 p-4 transition-[flex-grow] duration-500 ease-in-out sm:p-5 md:flex-row lg:h-[440px] lg:basis-0",
        isActive
          ? "lg:grow-[3]"
          : "lg:grow lg:gap-0 lg:bg-transparent lg:p-0",
      )}
    >
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={testimonial.image}
        alt={testimonial.author}
        className={cn(
          "h-72 w-full rounded-2xl object-cover md:h-auto md:min-h-80 md:w-2/5 lg:h-full lg:min-h-0",
          !isActive && "lg:w-full lg:rounded-none",
        )}
      />
      <figure
        className={cn(
          "animate-in fade-in flex flex-1 flex-col justify-center gap-8 duration-700 md:pe-4",
          !isActive && "lg:hidden",
        )}
      >
        <blockquote className="text-xl xl:text-2xl text-neutral-800 text-pretty">
          “{testimonial.quote}”
        </blockquote>
        <figcaption>
          <p className="text-lg font-medium text-neutral-900">
            {testimonial.author}
          </p>
          <p className="text-base text-neutral-700">{testimonial.role}</p>
        </figcaption>
      </figure>
      {!isActive && (
        <button
          type="button"
          onClick={handleSelect}
          aria-label={`Show testimonial from ${testimonial.author}`}
          className="absolute inset-0 hidden cursor-pointer lg:block"
        />
      )}
    </li>
  );
}

export function Testimonials({
  testimonials = quotes,
  title = "Testimonials",
  description = "" +
      "See how Zobliss helps beauty and wellness businesses grow",
}) {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.1 });
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <section className="relative overflow-hidden py-16 md:py-24" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: -40 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: -40 }}
          transition={{ duration: 1, delay: 0.2, ease: "easeInOut" }}
          className="relative flex items-end justify-between gap-6"
        >
          <SectionHeading title={title} description={description} />
          <MessageSquareQuote
            className="text-fe-primary/40 absolute -top-6 right-8 hidden size-40 lg:block"
            strokeWidth={0.5}
            aria-hidden="true"
          />
        </motion.div>
        <motion.ul
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
          transition={{ duration: 1, delay: 0.2, ease: "easeInOut" }}
          className="mt-12 flex flex-col gap-4 lg:flex-row"
        >
          {testimonials.map((testimonial, index) => (
            <TestimonialCard
              key={index}
              testimonial={testimonial}
              index={index}
              isActive={index === activeIndex}
              onSelect={setActiveIndex}
            />
          ))}
        </motion.ul>
      </div>
    </section>
  );
}
